import { useEffect, useState } from "react";
import type { ApiClient, Customer, OutboundMessageRequest, TimelineMessage } from "../api/client.js";

export function MessageTimeline(props: {
  api?: ApiClient;
  conversationId?: string;
  customer: Pick<Customer, "name" | "phoneNumber" | "whatsappLastSeenAt" | "whatsappPresenceExpiresAt" | "whatsappPresenceObservedAt" | "whatsappPresenceStatus">;
  messages: readonly TimelineMessage[];
  onMobileBack?: () => void;
  onMobileDetails?: () => void;
  outboundRequests?: readonly OutboundMessageRequest[];
}): React.JSX.Element {
  const requests = props.outboundRequests ?? [];
  const items = [
    ...props.messages.map((message) => ({ at: message.occurredAt, kind: "message" as const, message })),
    ...requests.filter((request) => request.status !== "SENT" && !request.linkedMessageId)
      .map((request) => ({ at: request.createdAt, kind: "request" as const, request }))
  ].sort((left, right) => left.at.localeCompare(right.at));
  return (
    <section aria-label="消息时间线" className="wa-timeline">
      <header>
        <div aria-hidden="true" className="wa-avatar wa-avatar--customer">{avatarLabel(props.customer.phoneNumber, props.customer.name)}</div>
        <div className="wa-timeline__identity"><h2>{props.customer.phoneNumber.startsWith("wa-lid:") ? "号码待解析" : props.customer.phoneNumber}</h2><small>{subtitle(props.customer.name)} · {presenceCopy(props.customer)}</small></div>
        {props.onMobileBack === undefined ? null : <button aria-label="返回会话列表" className="wa-mobile-control wa-mobile-control--back" onClick={props.onMobileBack} title="返回会话列表" type="button"><span aria-hidden="true">‹</span></button>}
        {props.onMobileDetails === undefined ? null : <button aria-label="查看客户详情" className="wa-mobile-control" onClick={props.onMobileDetails} title="查看客户详情" type="button"><span aria-hidden="true">i</span></button>}
      </header>
      {items.length === 0 ? <p>暂无消息。</p> : null}
      <ol>
        {items.map((item) => item.kind === "message" ? (() => {
          const message = item.message;
          const request = requests.find((entry) => entry.linkedMessageId === message.id || entry.confirmedMessageId === message.id);
          return (
          <li className={message.direction === "INBOUND" ? "wa-message wa-message--in" : "wa-message wa-message--out"} key={message.id}>
            <article>
              {message.type === "TEXT"
                ? <p>{message.text}</p>
                : <p aria-label={`${message.type} 安全占位`}>{message.placeholder}</p>}
              {message.type === "TEXT" ? null : <AttachmentDetails api={props.api} conversationId={props.conversationId} message={message} request={request} />}
              <time dateTime={message.occurredAt}>
                {new Date(message.occurredAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                {deliveryMarker(message)}
              </time>
              {request !== undefined && request.status !== "SENT" ? <small>{outboundStatusCopy(request.status)}</small> : null}
            </article>
          </li>
          );
        })() : (
          <li className="wa-message wa-message--out wa-message--status" key={`outbound-request-${item.request.id}`}>
            <article aria-label="人工外发状态">
              <p>{item.request.body || (item.request.attachmentKind === "VIDEO" ? "[视频]" : "[图片]")}</p>
              <AttachmentDetails api={props.api} conversationId={props.conversationId} request={item.request} />
              <small>{outboundStatusCopy(item.request.status)}</small>
            </article>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AttachmentDetails(props: { api: ApiClient | undefined; conversationId: string | undefined; message?: TimelineMessage; request: OutboundMessageRequest | undefined }): React.JSX.Element | null {
  const attachment = props.message?.attachmentMetadata ?? props.request?.attachmentMetadata;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (attachment === undefined || props.api === undefined || props.conversationId === undefined) return;
    let disposed = false;
    let objectUrl: string | null = null;
    const preview = props.message?.direction === "INBOUND"
      ? props.api.previewInboundMedia(props.conversationId, props.message.id)
      : props.api.previewMedia(props.conversationId, attachment.id);
    void preview.then((blob) => {
      if (disposed) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }, () => { if (!disposed) setUrl(null); });
    return () => { disposed = true; if (objectUrl !== null) URL.revokeObjectURL(objectUrl); };
  }, [attachment?.id, props.api, props.conversationId, props.message?.id, props.message?.direction]);
  if (attachment === undefined) return null;
  return <div className="wa-media-preview">
    {url === null ? null : attachment.kind === "IMAGE"
      ? <a href={url} rel="noopener noreferrer" target="_blank"><img alt={attachment.displayName ?? "发送的图片"} loading="lazy" src={url} /></a>
      : <video aria-label={attachment.displayName ?? "发送的视频"} controls preload="metadata" src={url} />}
    <small>{attachment.displayName ?? "附件"} · {attachment.contentType} · {(attachment.byteSize / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB</small>
  </div>;
}

function presenceCopy(customer: Pick<Customer, "whatsappLastSeenAt" | "whatsappPresenceExpiresAt" | "whatsappPresenceObservedAt" | "whatsappPresenceStatus">): string {
  const expiresAt = customer.whatsappPresenceExpiresAt === undefined || customer.whatsappPresenceExpiresAt === null
    ? 0
    : Date.parse(customer.whatsappPresenceExpiresAt);
  const observedAt = customer.whatsappPresenceObservedAt === undefined || customer.whatsappPresenceObservedAt === null
    ? NaN : Date.parse(customer.whatsappPresenceObservedAt);
  if (Number.isFinite(observedAt) && customer.whatsappPresenceStatus === "ONLINE" && expiresAt > Date.now()) {
    return `在线 · 更新于 ${new Date(observedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  const lastSeenAt = customer.whatsappLastSeenAt === undefined || customer.whatsappLastSeenAt === null
    ? NaN : Date.parse(customer.whatsappLastSeenAt);
  if (Number.isFinite(observedAt) && Number.isFinite(lastSeenAt) && lastSeenAt <= Date.now()) {
    return `最后已知上线 ${new Date(lastSeenAt).toLocaleString()}`;
  }
  return "在线状态不可用";
}

function subtitle(name: string): string {
  const normalized = name.trim();
  return normalized === "" || /^whatsapp(?:\s|$)/iu.test(normalized)
    ? "WhatsApp 客户"
    : normalized;
}

function avatarLabel(phoneNumber: string, name: string): string {
  if (phoneNumber.startsWith("wa-lid:")) return "?";
  const digits = phoneNumber.replace(/\D/gu, "");
  if (digits.length >= 2) return digits.slice(-2);
  return name.trim().slice(0, 1).toLocaleUpperCase() || "?";
}

function outboundStatusCopy(status: OutboundMessageRequest["status"]): string {
  switch (status) {
    case "PENDING": return "已提交，等待 WhatsApp 确认。";
    case "SENDING": return "正在等待 WhatsApp 确认。";
    case "NEEDS_HUMAN_REVIEW": return "WhatsApp 未确认；请先在手机端核对。系统不会自动重发。";
    case "BLOCKED": return "消息未发送：联系策略或 WhatsApp Session 已阻断该请求。";
    case "SENT": return "WhatsApp 已确认发送。";
  }
}

function deliveryMarker(message: TimelineMessage): string {
  if (message.direction !== "OUTBOUND") return "";
  // Do not draw WhatsApp-style confirmation ticks for a merely synchronized
  // or historical outbound record. The explicit sender marks it SENT only
  // after the provider supplies an external message ID.
  return message.status === "SENT" ? " · WhatsApp 已确认投递 ✓✓" : " · 已同步记录";
}
